import { IPage } from "../Components/Page";
import { EventCheckMode, eventStore, IStoreEvent } from "../Services/EventsStore";
import session from "../Services/Session";
import Home from "./Home";
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { Fragment, ReactElement, useState } from "react";
import { ticketValidator } from "../Services/TicketValidator";
import { formatDate, useInitAsync } from "../Utils";
import { useNetwork } from "../Hooks/Network";
import { appSettings } from "../AppSettings";
import { Button, LinearProgress, Typography } from "@mui/material";
import Title from "../Components/Title";
import { useParams } from "react-router";

import "./OfflineScans.scss"
import Field from "../Components/Field";
import { delayAsync } from "../Scanner/Utils";

export interface IOfflineScansProps {

    event?: IStoreEvent;
}

interface ITicketListInfo {
    count: number;
    lastUpdateTime: Date;
}

interface ISyncTicketState {
    isActive: boolean;
    isCompleted: boolean;
    total?: number;
    totalValid?: number;
    sync?: number;
    syncErrors?: number;
    invalidTickets?: number;
}

let syncState: ISyncTicketState;

let setSyncState: { (value: ISyncTicketState): void };

export function OfflineScansContent(props: IOfflineScansProps) {

    const network = useNetwork();

    const [ticketListInfo, setTicketListInfo] = useState(null as ITicketListInfo);

    [syncState, setSyncState] = useState({ isActive: false, isCompleted: false } as ISyncTicketState);


    const [event, setEvent] = useState(props.event);

    let { eventId } = useParams();

    if (event)
        eventId = event.id;

    const updateTicketListInfoAsync = async () => {
        const list = await ticketValidator.getOfflineListAsync(eventId);

        setTicketListInfo({
            count: list?.tickets?.length ?? 0,
            lastUpdateTime: list?.lastUpdateTime
        });
    }

    const updateSyncStateAsync = async (newState?: Partial<ISyncTicketState> ) => {

        const result = await ticketValidator.getScansToSyncAsync(eventId);

        const total = result.ticketsToSync?.length ?? 0;

        const value = {
            ...syncState,
            ...newState,
            total,
            totalValid: result.totalValidToSync
        } as ISyncTicketState;

        setSyncState(value);

        return total;
    }


    const syncScansAsync = async () => {

        const curState = {
            total: await updateSyncStateAsync({ isActive: true, isCompleted: false}),
            isCompleted: false,
            isActive: true,
            invalidTickets: 0,
            sync: 0,
            syncErrors: 0
        } as ISyncTicketState;

        await delayAsync(0);

        const result = await ticketValidator.syncScansAsync(eventId, (ticket, result) => {

            curState.isActive = syncState.isActive;

            curState.sync++;

            if (!result)
                curState.syncErrors++;

            if (result?.type == "KO")
                curState.invalidTickets++;

            setSyncState({
                ...curState
            });

            return syncState.isActive;
        });

        await updateSyncStateAsync({ isActive: false, isCompleted: true });
    }

    const cancelSyncAsync = async () => {

        await updateSyncStateAsync({ isActive: false, isCompleted: true });
    }


    useInitAsync(() => network(async () => {

        if (!event && eventId) {
            session.activeEvent = await eventStore.getEventByIdAsync(eventId);
            setEvent(session.activeEvent);
        }

        if (eventId) {
            await updateTicketListInfoAsync();
            await updateSyncStateAsync();
        }     
    }));

    const updateTicketList = () => {

        network(async () => {
            await ticketValidator.syncValidTicketsAsync(eventId);
            await updateTicketListInfoAsync();
        });
    }

    let body: ReactElement;

    if (!ticketListInfo)
        body = null;

    else if (!appSettings.offlineMode)
        body = <Typography className="message">Modalità offline non attiva</Typography>; 

    else {
        body = <>
            <div className="panel">
                <fieldset>
                    <Field key="0" label="Titoli offline">{ticketListInfo?.count ?? 0}</Field>
                    <Field key="1" label="Aggiornato il">{ticketListInfo?.lastUpdateTime ? formatDate(ticketListInfo.lastUpdateTime) : "Mai"}</Field>
                </fieldset>
                <div className="actions">
                    <Button variant="contained" className="change-pin" size="large" onClick={() => updateTicketList()}>Aggiorna Lista Titoli</Button>
                </div>
            </div>

            <div className="panel">
                {syncState?.total == 0 && !syncState.isActive && !syncState.isCompleted ?
                    <Typography className="message">Nessun titolo da trasmettere</Typography> :
                    (<fieldset>
                        <Field key="0" label="Da Trasmettere Totali">{syncState.total}</Field>
                        <Field key="1" label="Da Trasmettere Validi">{syncState.totalValid}</Field>
                        {(syncState.isActive || syncState.isCompleted) && <>
                            <Field key="2" label="Trasmessi">{(syncState.sync ?? 0)}</Field>
                            <Field key="3" label="Errori">{syncState.syncErrors ?? 0}</Field>
                            <Field key="4" label="Invalidi">{syncState.invalidTickets ?? 0 }</Field>
                        </>}
                    </fieldset>)
                }

                {(syncState.isActive) && 
                    <LinearProgress value={syncState.sync / syncState.total} variant="determinate" />
                }

                <div className="actions">
                    {syncState?.total > 0 && !syncState.isActive && <Button variant="contained" className="change-pin" size="large" onClick={() => syncScansAsync()}>Trasmetti letture</Button>}
                    {syncState.isActive && <Button variant="contained" className="change-pin" size="large" onClick={() => cancelSyncAsync()}>Annulla</Button>}
                </div>
            </div>
        </>
    }

    return <>
        <header>
            <div className="sub-title">Titoli Offline</div>
            <Title>{event?.title}</Title>
        </header>
        {body}
    </>
}

const OfflineScans: IPage = {

    name: "offline-scans",
    title: "Scansioni offline",
    route: "/offline/:eventId",
    icon: <WifiOffIcon />,
    container: Home,
    content: <OfflineScansContent />,
    isEnabled: () => session.activeEvent != null && session.activeGate != null && session.activeEvent.checkMode == EventCheckMode.Scan,
    appBar: { title: "Access Control", backVisible: true } 
}

export default OfflineScans;